/**
 * The one lock on MSAL interactive APIs — see docs/specs/7665-logout-interaction-in-progress/spec.md.
 *
 * MSAL permits a single interaction at a time and rejects the rest with `interaction_in_progress`.
 * Sign-out, silent renewal and the interactive sign-in fallback all pass through here, so they can
 * never fight each other.
 *
 * Module scope, not React state: the callers live in different component subtrees, and one of them
 * (the Apollo links) is not a component at all.
 */
let heldBy: string | null = null
let followUp: (() => void) | null = null

/** True when the caller now owns the lock. A losing caller may {@link queueInteraction}. */
export function acquireInteraction(label: string): boolean {
  if (heldBy) {
    console.info(`Auth interaction "${label}" declined: "${heldBy}" is already in progress.`)
    return false
  }
  heldBy = label
  return true
}

/**
 * Releases the lock and runs whatever was queued behind it.
 *
 * A *redirect* deliberately never calls this: the page is on its way out, and releasing would
 * reopen the race during teardown. A queued follow-up behind a redirect therefore never runs,
 * which is the correct outcome — there is nothing left to follow up on.
 */
export function releaseInteraction(): void {
  heldBy = null
  const queued = followUp
  followUp = null
  queued?.()
}

/**
 * Leaves one action to run when the current interaction finishes. Later calls while one is already
 * queued are dropped — the queue is a hand-off slot, not a backlog.
 */
export function queueInteraction(action: () => void): void {
  if (followUp) {
    return
  }
  followUp = action
}
